import { createSelector, createSlice } from '@reduxjs/toolkit';

const STORAGE_KEY = 'cobranza-auth';

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persist(accessToken, refreshToken) {
  try {
    if (accessToken && refreshToken) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ accessToken, refreshToken }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (private mode, quota, etc.) - session just won't survive a reload
  }
}

const persisted = loadPersisted();

// status: 'unauthenticated' | 'loading' (tokens present, profile not fetched yet) | 'authenticated'
const initialState = {
  accessToken: persisted?.accessToken ?? null,
  refreshToken: persisted?.refreshToken ?? null,
  user: null,
  role: null,
  status: persisted?.accessToken ? 'loading' : 'unauthenticated'
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Right after login: tokens are known, profile/permissions are not yet.
    credentialsSet(state, action) {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.status = 'loading';
      persist(accessToken, refreshToken);
    },
    // After a silent refresh: same shape, but never resets status/profile.
    credentialsUpdated(state, action) {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      persist(accessToken, refreshToken);
    },
    profileLoaded(state, action) {
      const { user, role } = action.payload;
      state.user = user;
      state.role = role;
      state.status = 'authenticated';
    },
    loggedOut(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.role = null;
      state.status = 'unauthenticated';
      persist(null, null);
    }
  }
});

export const { credentialsSet, credentialsUpdated, profileLoaded, loggedOut } = authSlice.actions;
export default authSlice.reducer;

export const selectAccessToken = (state) => state.auth.accessToken;
export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectAuthStatus = (state) => state.auth.status;
export const selectCurrentUser = (state) => state.auth.user;
export const selectCurrentRole = (state) => state.auth.role;

// Memoized: without this, .map() returns a new array on every call, so any
// component reading it via useSelector re-renders on every unrelated store update.
export const selectPermissionCodes = createSelector(
  [selectCurrentRole],
  (role) => role?.permissions?.map((permission) => permission.code) ?? []
);
